/**
 * @copyright Copyright 2023-2023 Epic Systems Corporation
 * @file Download info for an app and it's versions
 * @module Epic.AppOrchard.Showroom.DownloadStatusSection
 */

import { WarningTwoIcon } from "@chakra-ui/icons";
import {
	Box,
	Flex,
	Heading,
	Table,
	TableContainer,
	Tag,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
} from "@chakra-ui/react";
import { AOLink } from "ao/components/Core";
import { isAutoDownloaded } from "ao/components/Showroom/Helper";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { FunctionComponent, memo } from "react";
import { AppKeyDownloads } from "../AppHelperComponents";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	headerColor: string;
	isShowroom?: boolean;
}

export const DownloadStatusSection: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, headerColor, isShowroom } = props;

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const appModel = appDetails.appModel;

	const appId = appModel.id;

	const newestVersion = appDetails.appVersions[appDetails.appVersions.length - 1];
	const hasNewerVersion = appId < newestVersion.id;

	const hasNewestVersionDownload =
		(appDetails.prodVersion && appDetails.prodVersion.id === newestVersion.id) ||
		(appDetails.requestedVersion && appDetails.requestedVersion.id === newestVersion.id) ||
		(appDetails.nonProdVersion && appDetails.nonProdVersion.id === newestVersion.id);

	const isLicenseInBoth =
		appDetails.prodVersion &&
		appDetails.nonProdVersion &&
		appDetails.nonProdVersion.id === appDetails.prodVersion.id;

	const hasVersionDownload =
		appDetails.isDownloaded ||
		appDetails.requestedVersion !== null ||
		appDetails.nonProdVersion !== null ||
		appDetails.prodVersion !== null;

	const justify = "center";
	const fontFamily = isShowroom ? "Jost !important" : "unset";

	const showroomUrl = isShowroom ? siteInfo.showroomRelativeUrl : siteInfo.showroomUrl;
	const vendorServicesUrl = isShowroom ? siteInfo.vendorServicesUrl : "/";

	return (
		<>
			{(appDetails.isDownloaded || hasVersionDownload) && (
				<Box w="100%">
					{appModel.isEpicOnFhirApp ? (
						<>
							{appDetails.isDownloaded && (
								<>
									<Heading
										as="h3"
										fontWeight="normal"
										fontSize="1.5rem"
										fontFamily={fontFamily}
										textAlign={justify}
										mb="10px"
										color={headerColor}
									>
										Download Status
									</Heading>
									<Flex justify={justify} mb="5px">
										<AppKeyDownloads
											downloadApprovedStatus={appDetails.vendorApproved}
											appStatus={appModel.status}
										/>
									</Flex>
									<Text textAlign={justify}>
										{isAutoDownloaded(appDetails.downloadedBy)
											? `Auto downloaded on ${appDetails.downloadedOn}`
											: `Download requested by ${appDetails.downloadedBy} on ${appDetails.downloadedOn}`}
									</Text>
								</>
							)}
						</>
					) : (
						<>
							{hasVersionDownload && (
								<>
									<Heading
										as="h3"
										fontFamily={fontFamily}
										textAlign={{ base: "center", lg: "left" }}
										mb="10px"
										color={headerColor}
									>
										Versions
									</Heading>

									{hasNewerVersion && (
										<Text pl="14px" pb="10px">
											<WarningTwoIcon color="red.600" mr="3px" />
											Your organization has requested or downloaded other versions of
											this product. Only one version of a product can be active at a
											time in a given Epic environment.
										</Text>
									)}
									<TableContainer>
										<Table size="sm">
											<Thead>
												<Tr>
													<Th fontFamily={fontFamily}>Status</Th>
													<Th fontFamily={fontFamily}>Version</Th>
													<Th fontFamily={fontFamily}>Link</Th>
													<Th fontFamily={fontFamily}>Download Requested By</Th>
													<Th fontFamily={fontFamily}>Download Requested On</Th>
												</Tr>
											</Thead>
											<Tbody>
												{hasNewerVersion && !hasNewestVersionDownload && (
													<Tr>
														<Td>
															<Tag
																size="md"
																variant="showroom"
																colorScheme="white"
																textAlign="center"
															>
																Newest version
															</Tag>
														</Td>
														<Td>{newestVersion.name}</Td>
														<Td>
															<AOLink
																url={
																	newestVersion.isInShowroom
																		? `${showroomUrl}listing?appId=${newestVersion.id}`
																		: `${vendorServicesUrl}Download?id=${newestVersion.id}`
																}
															>
																Click to see this version
															</AOLink>
														</Td>
														<Td fontStyle="italic">(Not picked)</Td>
														<Td fontStyle="italic">(Not picked)</Td>
													</Tr>
												)}
												{appDetails.prodVersion &&
													appDetails.prodVersion.id !== appId && (
														<Tr>
															<Td>
																<AppKeyDownloads
																	downloadApprovedStatus={
																		appDetails.prodVersion.vendorApproved
																	}
																	isProdOnly={!isLicenseInBoth}
																/>
															</Td>
															<Td>{appDetails.prodVersion.name}</Td>
															<Td>
																<AOLink
																	url={
																		appDetails.prodVersion.isInShowroom
																			? `${showroomUrl}listing?appId=${appDetails.prodVersion.id}`
																			: `${vendorServicesUrl}Download?id=${appDetails.prodVersion.id}`
																	}
																>
																	Click to see this version
																</AOLink>
															</Td>
															<Td>{appDetails.prodVersion.licensedBy}</Td>
															<Td>{appDetails.prodVersion.licensedOn}</Td>
														</Tr>
													)}
												{appDetails.nonProdVersion &&
													!isLicenseInBoth &&
													appDetails.nonProdVersion.id !== appId && (
														<Tr>
															<Td>
																<AppKeyDownloads
																	downloadApprovedStatus={
																		appDetails.nonProdVersion
																			.vendorApproved
																	}
																/>
															</Td>
															<Td>{appDetails.nonProdVersion.name}</Td>
															<Td>
																<AOLink
																	url={
																		appDetails.nonProdVersion.isInShowroom
																			? `${showroomUrl}listing?appId=${appDetails.nonProdVersion.id}`
																			: `${vendorServicesUrl}Download?id=${appDetails.nonProdVersion.id}`
																	}
																>
																	Click to see this version
																</AOLink>
															</Td>
															<Td>{appDetails.nonProdVersion.licensedBy}</Td>
															<Td>{appDetails.nonProdVersion.licensedOn}</Td>
														</Tr>
													)}
												{appDetails.requestedVersion &&
													appDetails.requestedVersion.id !== appId && (
														<Tr>
															<Td>
																<AppKeyDownloads
																	downloadApprovedStatus={
																		appDetails.requestedVersion
																			.vendorApproved
																	}
																/>
															</Td>
															<Td>{appDetails.requestedVersion.name}</Td>
															<Td>
																<AOLink
																	url={
																		appDetails.requestedVersion
																			.isInShowroom
																			? `${showroomUrl}listing?appId=${appDetails.requestedVersion.id}`
																			: `${vendorServicesUrl}Download?id=${appDetails.requestedVersion.id}`
																	}
																>
																	Click to see this version
																</AOLink>
															</Td>
															<Td>{appDetails.requestedVersion.licensedBy}</Td>
															<Td>{appDetails.requestedVersion.licensedOn}</Td>
														</Tr>
													)}
												{appDetails.isDownloaded && (
													<Tr>
														<Td>
															<AppKeyDownloads
																downloadApprovedStatus={
																	appDetails.vendorApproved
																}
															/>
														</Td>
														<Td>{appModel.version}</Td>
														<Td>This is the version shown on this page</Td>
														<Td>{appDetails.downloadedBy}</Td>
														<Td>{appDetails.downloadedOn}</Td>
													</Tr>
												)}
											</Tbody>
										</Table>
									</TableContainer>
								</>
							)}
						</>
					)}
				</Box>
			)}
		</>
	);
});
